import { Component, EventEmitter, Output, Input, SimpleChanges, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GridSelectionModel, SearchFormType } from '@se/platform';
import { NotificationService, ButtonClick, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import { RowEvent } from 'ag-grid-community';
import moment from 'moment';

import { PatientCertificationService, PatientToSearch } from './services/patient-certification.service';
import { PatientCertificationGridService } from './services/patient-certification-grid.service';
import { Patient as EstensaPatient } from 'estensa-module';
import { BaseExamination } from '@se/models';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'patient-certification',
    templateUrl: './patient-certification.component.html',
    styleUrls: ['./patient-certification.component.scss'],
})
export class PatientCertificationComponent implements OnChanges {
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() selectedExamination: BaseExamination;
    @Input() showDialog: boolean;
    @Input() formType: SearchFormType;

    items: EstensaPatient[];
    sexes: any[];
    gridOptions: any;
    loading = false;
    message: string;

    patientToSearch: PatientToSearch = new PatientToSearch('', '', null, '', null);

    private _patient: EstensaPatient;

    get patient(): EstensaPatient {
        return this._patient;
    }

    constructor(
        private patientCertificationService: PatientCertificationService,
        private translate: TranslateService,
        private notificationService: NotificationService,
        public modalDialogNotificationService: ModalDialogNotificationService,
        private patientCertificationGridService: PatientCertificationGridService
    ) {
        this.patientCertificationService.getSexes().subscribe((sexes) => {
            this.sexes = sexes;
        });

        this.gridOptions = {
            rowSelection: GridSelectionModel.single,
            suppressCellFocus: true,
            suppressLoadingOverlay: true,
            columnDefs: patientCertificationGridService.buildColumns(),
            defaultColDef: { editable: false, sortable: true, resizable: true },
            enableBrowserTooltips: true,
        };
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showDialog && changes.showDialog.currentValue) {
            this.onOpen();
        }
    }

    onSearch() {
        const selectedPatientBirthDate = this.selectedExamination.patient.birthDate as any;
        if (
            selectedPatientBirthDate &&
            this.patientToSearch.birthDate &&
            this.patientToSearch.birthDate != selectedPatientBirthDate
        ) {
            this.patientToSearch.birthDate = moment(this.patientToSearch.birthDate).utc().toDate(); //moment(this.patientToSearch.birthDate).utc().add(new Date().getTimezoneOffset(), 'm');
        }

        this.patientCertificationGridService
            .search({
                firstName: this.patientToSearch.firstName,
                lastName: this.patientToSearch.lastName,
                sex: this.patientToSearch.sex,
                taxCode: this.patientToSearch.taxCode,
                birthDate: this.patientToSearch.birthDate,
                searchMasterDataOrderType: 0,
            })
            .subscribe({
                next:(items) => {
                    this.loading = false;
                    this.items = items;
                    this.message = '';
                },
                error: (exception) => {
                    this.loading = false;
                    this.message = this.lookupErrorMessage(exception);
                    this._patient = undefined;
                    this.items = [];
                }
            });
    }

    onResetSex() {
        this.patientToSearch.sex = null;
    }

    onResetDate() {
        this.patientToSearch.birthDate = null;
    }

    onSelectionChange(e: RowEvent) {
        if (e.api.getSelectedRows().length === 1) {
            this._patient = e.api.getSelectedRows()[0];
        } else {
            this._patient = undefined;
        }
    }

    compareIds(source: any, target: any) {
        if (!source && !target) {
            return true;
        }

        return source && target && source.id === target.id;
    }

    canCertificate(): boolean {
        return !!this._patient && !this.loading;
    }

    canImport(): boolean {
        return !!this._patient && this._patient.isLocal && !this.loading; // Vale: per ora togliamo Importa e Associa perché non vengono ancora caricati i pazienti presenti solo in anagrafica remota
    }

    async onCertificate() {
        if (
            this.selectedExamination.patient.lastName != this.patient.lastName ||
            this.selectedExamination.patient.firstName != this.patient.firstName ||
            this.selectedExamination.patient.birthDate != this.patient.birthDate ||
            this.selectedExamination.patient.sex != this.patient.sex as any ||
            this.selectedExamination.patient.taxCode != this.patient.taxCode
        ) {
            let buttonClick: ButtonClick = await firstValueFrom(this.modalDialogNotificationService
                .notify({
                    buttonYes: true,
                    buttonNo: true,
                    buttonCancel: false,
                    buttonOk: false,
                    title: this.translate.instant('Warning'),
                    message:
                        this.translate.instant('ECG_ConnectingPatientConfirm') + ' ' +
                        this.selectedExamination.patient.lastName?.toUpperCase() + ' ' +
                        this.selectedExamination.patient.firstName?.toUpperCase() + ' ' +
                        this.translate.instant('ECG_ConnectingPatientConfirm_1') + ' ' +
                        this.patient.lastName?.toUpperCase() + ' ' +
                        this.patient.firstName?.toUpperCase() + ' ' +
                        this.translate.instant('ECG_ConnectingPatientConfirm_2'),
                    type: NotificationType.warning,
                })
            );

            if (buttonClick !== ButtonClick.yes) {
                return;
            }
        }


        this.patientCertificationService
            .certificate(this.formType, this.selectedExamination.patient.idLocalPatient, this.patient.id, this.selectedExamination.id)
            .subscribe({
                next: () => {
                    this.onClose(true);
                    this.notificationService.success('AssociateSuccessful');
                },
                error:   (exception) => {
                    this.loading = false;
                    this.message = this.lookupErrorMessage(exception);
                    this._patient = undefined;
                    this.items = [];
                }
            });
    }

    onClose(refreshReportList: boolean) {
        this.loading = false;
        this.message = '';
        this._patient = undefined;
        this.items = [];
        this.patientToSearch.setPatientToSearch('', '', null, '', null);
        this.closeDialog.emit(refreshReportList);
    }

    private lookupErrorMessage(exception) {
        if (exception && exception.error && exception.error.estensaFaultDetails) {
            if (exception.error.estensaFaultDetails.typeName === 'TooMuchResultFault') {
                return this.translate.instant(exception.error.estensaFaultDetails.typeName, {
                    p0: exception.error.estensaFaultDetails.estensaFault.count,
                    p1: exception.error.estensaFaultDetails.estensaFault.limit,
                    p2: ' ',
                });
            }
            return this.translate.instant('AssociateFailed') + ' ' + exception.message;
        }
    }

    private onOpen() {
        if (!this.selectedExamination) {
            return;
        }
        const patient = this.selectedExamination.patient;
        if (patient && patient.isLocalPatient) {
            this.patientToSearch.setPatientToSearch(
                patient.firstName?.toUpperCase(),
                patient.lastName?.toUpperCase(),
                null, // Non prevalorizare il sesso perché alcuni ecg vengono prevalorizzati con il valore sbagliato
                patient.taxCode?.toUpperCase(),
                patient.birthDate
            );
            this.onSearch();
        }
    }
}
